import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, NA_LABEL } from "src/constants/constants";
import { Button, Column, Icon, Loader, Row, Sheet, Text } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { InspectionResult, isOverridden, resolveDisplayedComment } from "src/services/inspectionService";
import "./ComplianceDetailSheet.scss";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import EvidenceTabs from "./EvidenceTabs";
import { isResultNA } from "src/components/pages/inspection/utils";

/**
 * Returns true when a result's confirmedResult is a numeric value (e.g. "4")
 * rather than a boolean-style "Yes"/"No". Auto-calculated results can produce
 * numeric confirmed values that should be displayed as-is.
 */
function isNumericResult(result: InspectionResult): boolean {
    return result.confirmedResult !== null
        && result.confirmedResult !== undefined
        && result.confirmedResult !== ""
        && !isNaN(Number(result.confirmedResult));
}

interface ComplianceDetailSheetProps {
    open: boolean;
    onClose: () => void;
    result: InspectionResult | null;
    presignedUrlMap: Record<string, string>;
    onOverride?: (result: InspectionResult) => void;
    /** Human-readable question text from the workflow question config. */
    questionText?: string;
    /** Whether the question was answered by AI. Derived from the workflow question. */
    isAI?: boolean;
    /** Whether the question is auto-calculated. Derived from the workflow question mode. */
    isAutoCalculated?: boolean;
}

const ComplianceDetailSheet: React.FC<ComplianceDetailSheetProps> = ({
    open,
    onClose,
    result,
    presignedUrlMap,
    onOverride,
    questionText,
    isAI = false,
    isAutoCalculated = false,
}) => {
    const [expandedUrl, setExpandedUrl] = useState<string | null>(null);
    const [loadedThumbnails, setLoadedThumbnails] = useState<Record<string, boolean>>({});
    const [activeTab, setActiveTab] = useState<"overridden" | "original">("overridden");

    // Close the portaled lightbox when the sheet closes or switches to a
    // different result, otherwise it can stay orphaned on screen / show a stale image.
    useEffect(() => {
        setExpandedUrl(null);
        setLoadedThumbnails({});
        setActiveTab("overridden");
    }, [open, result?.objectId]);

    if (!result) return null;

    const evidenceUrls = result.evidence
        .map((e) => presignedUrlMap[e.uri])
        .filter(Boolean);

    const shouldShowTabs = isOverridden(result)
        && result.evidence.length > 0;

    const isAnalysisFailed = result.aiResult?.toUpperCase() === "FAILED";
    const isAnalysisPending = result.aiResult?.toUpperCase() === "PENDING";
    const isNA = isResultNA(result);

    const isOriginalTabActive = shouldShowTabs && activeTab === "original";
    
    // originalIsCompliant is the compliance state before the override was applied (the AI's original determination).
    // It is only present when the result has been overridden. When the result is not overridden,
    // isCompliant itself represents the original (and current) value.
    const displayCompliant = isOriginalTabActive && result.originalIsCompliant !== undefined ? result.originalIsCompliant : result.isCompliant;

    const themedTagText = isAnalysisPending ? "Analyzing..."
        : isAnalysisFailed ? "Failed"
            : isNA ? NA_LABEL
                : displayCompliant ? COMPLIANT_LABEL
                    : NON_COMPLIANT_LABEL;

    const themedTagType = isAnalysisPending ? "neutral"
        : isNA ? "gray"
            : displayCompliant ? "success"
                : "error";

    const canOverride = !isAnalysisPending && !isAnalysisFailed && !isNA;

    // Answer source, mirroring the tag on ReviewCheckpointCard so the sheet and the
    // card never disagree. Both values come from the workflow question config.
    const sourceTagText = (isAutoCalculated ? "Auto" : isAI ? "AI" : "Manual").toUpperCase();

    const commentText = resolveDisplayedComment(
        isOverridden(result), isOriginalTabActive, result.overrideReason, result.userComment
    );

    return (
        <>
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">Compliance Details</Text>
                    <Button onClick={onClose} type="icon">
                        <Icon tokens={closeSmallTokens}>Close compliance details</Icon>
                    </Button>
                </Row>

                {questionText && (
                    <Text type="b200" color="secondary">
                        {questionText}
                    </Text>
                )}

                {result.objectId !== null && (
                    <Text type="h200" color="primary">
                        {result.objectId}
                    </Text>
                )}

                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="b200" color="primary">
                        Answer : {isNumericResult(result) ? result.confirmedResult : displayCompliant ? "YES" : "NO"}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        <ThemedTag text={themedTagText} type={themedTagType} />
                        {/* Answer source tag is only meaningful once analysis has settled. */}
                        {!isAnalysisPending && <ThemedTag text={sourceTagText} type="neutral" />}
                    </Row>
                </Row>

                {isOverridden(result) && activeTab !== "original" && (
                    <div style={{ alignSelf: 'flex-start' }}>
                        <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                    </div>
                )}

                {isAnalysisPending ? (
                    <Loader type="circular" size="large" />
                ) : (
                    <>
                        {(!shouldShowTabs || activeTab === "original") && result.aiReasoning && result.aiReasoning !== "" && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason</Text>
                                <Text type="b200" color="primary">{result.aiReasoning}</Text>
                            </Column>
                        )}

                        {shouldShowTabs ? (
                            <EvidenceTabs
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                                overriddenEvidences={result.overriddenEvidences ?? []}
                                originalEvidences={result.evidence}
                                presignedUrlMap={presignedUrlMap}
                                onImageExpand={setExpandedUrl}
                            />
                        ) : evidenceUrls.length > 0 && (
                            <Row spacing="300" wrap="down">
                                {evidenceUrls.map((url, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="compliance-detail-sheet__image-button"
                                        onClick={() => setExpandedUrl(url)}
                                        aria-label={`Expand evidence ${idx + 1}`}
                                    >
                                        <img
                                            src={url}
                                            alt={`Evidence ${idx + 1}`}
                                            className={`compliance-detail-sheet__image${evidenceUrls.length === 1 ? " compliance-detail-sheet__image--large" : ""}`}
                                            onLoad={() => setLoadedThumbnails((prev) => ({ ...prev, [url]: true }))}
                                        />
                                        {loadedThumbnails[url] && (
                                            <span className="compliance-detail-sheet__image-hint">
                                                <span aria-hidden="true" className="compliance-detail-sheet__image-hint-icon">⤢</span>
                                                Expand
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </Row>
                        )}

                        {commentText && (
                            <Column spacing="100">
                                <Text type="h200" color="primary">Comments</Text>
                                <Text type="b200" color="secondary">{commentText}</Text>
                            </Column>
                        )}

                        {canOverride && onOverride && (
                            <button
                                className="compliance-detail-sheet__override-btn"
                                onClick={() => onOverride(result)}
                            >
                                MANUAL OVERRIDE
                            </button>
                        )}
                    </>
                )}
            </Column>
        </Sheet>

        {expandedUrl && ReactDOM.createPortal(
            <div
                className="compliance-detail-sheet__lightbox"
                aria-label="Expanded evidence image"
                onClick={() => setExpandedUrl(null)}
            >
                <button
                    type="button"
                    className="compliance-detail-sheet__lightbox-close"
                    onClick={() => setExpandedUrl(null)}
                    aria-label="Close expanded image"
                >
                    &times;
                </button>
                <img
                    src={expandedUrl}
                    alt="Expanded evidence"
                    className="compliance-detail-sheet__lightbox-image"
                    onClick={(e) => e.stopPropagation()}
                />
            </div>,
            document.body
        )}
        </>
    );
};

export default ComplianceDetailSheet;
